import {Box, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import Card from "./Card";
import {ArticleWithMeta} from "../Util/useArticles";
import {Subject, subjectColours} from "../Util/constants";
import {camelCaseToNormal, capitaliseWords} from "../Util/util";

type Variant = "featured" | "row" | "compact";

type Props = {
    article: ArticleWithMeta,
    variant?: Variant,
    grid?: boolean,
    xs?: number,
    sm?: number,
    md?: number,
    lg?: number,
};

// A single article preview. `featured` is the large hero card (icon + title +
// summary, subject-coloured top/bottom border), `row` is the standard list card
// used across Learn/News/subject grids, and `compact` is the small sidebar item.
const FALLBACK_COLOUR = "#666666";

export default function ArticleCard({article, variant = "row", grid, xs, sm, md, lg}: Props) {
    const navigate = useNavigate();
    const colour = subjectColours[article.subject.toLowerCase() as Subject] ?? FALLBACK_COLOUR;
    const open = () => navigate(`/article/${article.id}`);
    const author = capitaliseWords(camelCaseToNormal(article.author ?? ""));

    if (variant === "compact") {
        return (
            <Card onClick={open} grid={grid} xs={xs} sm={sm} md={md} lg={lg}
                contentSx={{p: "10px !important", borderLeft: `4px solid ${colour}`}}>
                <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                    {article.url && <Box component={"img"} src={article.url} width={"44px"} height={"44px"} sx={{borderRadius: "50%", flexShrink: 0}}/>}
                    <Typography variant={"subtitle2"} sx={{lineHeight: 1.2}}>{article.title}</Typography>
                </Stack>
            </Card>
        );
    }

    if (variant === "featured") {
        return (
            <Card onClick={open} grid={grid} xs={xs} sm={sm} md={md} lg={lg}
                sx={{borderTop: `7px solid ${colour}`, borderBottom: `7px solid ${colour}`}}
                contentSx={{p: 3}}>
                <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems={"center"}>
                    <Stack textAlign={"center"} spacing={0.5} sx={{flexShrink: 0}}>
                        {article.url && <Box component={"img"} src={article.url} width={"120px"} height={"120px"} sx={{borderRadius: "50%"}}/>}
                        <Typography variant={"caption"}>{author}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant={"h4"}>{article.title}</Typography>
                        <Typography sx={{opacity: 0.85}}>{article.summary}</Typography>
                    </Stack>
                </Stack>
            </Card>
        );
    }

    // "row" — the standard preview card.
    return (
        <Card onClick={open} grid={grid} xs={xs} sm={sm} md={md} lg={lg}
            contentSx={{borderLeft: `5px solid ${colour}`}}>
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
                <Stack spacing={0} textAlign={"center"} sx={{flexShrink: 0}}>
                    {article.url && <Box component={"img"} mb={0.5} src={article.url} width={"70px"} height={"70px"} sx={{borderRadius: "50%"}}/>}
                    <Typography variant={"caption"}>{author}</Typography>
                </Stack>
                <Stack spacing={0.5}>
                    <Typography variant={"h6"} sx={{lineHeight: 1.2}}>{article.title}</Typography>
                    <Typography variant={"body2"} sx={{opacity: 0.85}}>{article.summary}</Typography>
                </Stack>
            </Stack>
        </Card>
    );
}
